import { useApolloClient } from '@apollo/client';
import * as Clipboard from 'expo-clipboard';
import React, {
  ComponentProps,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { Linking } from 'react-native';
import Toast from 'react-native-toast-notifications';

import { useWebDrawer } from '@components/Web/Drawer/WebDrawerContext';
import { PopupIcon } from '@components/Web/PopupIcon';
import {
  GetMeDocument,
  Project,
  ProjectMemberRole,
  useArchiveProjectMutation,
  useCompleteWebTutorialFlowsMutation,
  useCreateInviteMutation,
  useLeaveProjectMutation,
  useUnarchiveProjectMutation,
  useUpdateProjectMutation,
  WebTutorialFlow,
  useDeleteProjectMutation,
  GetProjectDocument,
} from '@graphql/generated';
import useFeatureFlag from '@hooks/useFeatureFlag';
import useMe from '@hooks/useMe';
import { useProjectMembership } from '@hooks/useProjectMembership';
import { useWebDrawerNavigator } from '@hooks/useWebDrawerNavigator';
import { useAppNavigation } from '@navigation/useAppNavigation';
import { ALLOWED_FREE_PERSONAL_PROJECTS } from '@src/constants/constants';
import theme from '@themes/theme';
import { shouldShowPaywallForProject } from '@utils/teams/paywall';

import { ConfirmModal } from './ConfirmModal';

type Props = Pick<ComponentProps<typeof PopupIcon>, 'backgroundColor'> & {
  project: Project;
  popPosition?: string;
  popSize?: 'big' | 'small';
};

export const ProjectDetailModal = (props: Props) => {
  const navigation = useAppNavigation();

  const { setShowProjectColorAndIconModal, setSelectedProject } =
    useWebDrawerNavigator();
  const {
    setIsCreateProjectOpen,
    setEditProjectUUId,
    setPaywallModalOpen,
    setPaywallTeam,
  } = useWebDrawer();
  const [showLeaveProjectModal, setShowLeaveProjectModal] = useState(false);
  const [showErrorMsg, setShowErrorMsg] = useState('');
  const toastRef = useRef();
  const { listFeatureFlags } = useFeatureFlag();
  const [referencePinStatus, setReferencePinStatus] = useState<boolean | null>(
    null
  );
  const client = useApolloClient();

  const projectReportFlag = listFeatureFlags?.find(
    (flag: { name: string; enabled: boolean }) => flag.name === 'project_report'
  );
  const { t } = useTranslation('models');

  const { project, popPosition, popSize } = props;

  const {
    isMember,
    isViewer,
    isAdminOrHigher,
    isEditorOrHigher,
    isTeamExpired,
  } = useProjectMembership(project);
  const { me } = useMe();
  const isPersonalProjectsLeft =
    ALLOWED_FREE_PERSONAL_PROJECTS - (me?.personalProjectsCount ?? 0) > 0;

  const isOwner = me?.id === project?.creator?.id;
  const isTeamOwnerOrAdmin =
    project?.currentUserTeamRole === 'OWNER' ||
    project?.currentUserTeamRole === 'ADMIN';
  const isTeamMember = project?.currentUserTeamRole === 'MEMBER';
  const isProjectAdmin = project?.currentUserRole === ProjectMemberRole.Admin;
  const teamPersonal = project?.team?.personal ?? false;
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);

  const [leaveProject, { data: leaveProjectData }] = useLeaveProjectMutation({
    refetchQueries: ['listProjects'],
  });

  useEffect(() => {
    if (!leaveProjectData) return;
    navigation.navigate('projects-stack', { screen: 'allprojects' });
  }, [leaveProjectData]);

  const [
    archiveProject,
    { data: archiveProjectData, error: archiveProjectError },
  ] = useArchiveProjectMutation({
    refetchQueries: ['listProjects'],
  });

  useEffect(() => {
    if (!archiveProjectData) return;
    if (archiveProjectData.archiveProject) {
      setSelectedProject(archiveProjectData.archiveProject as Project);
    }
    navigation.navigateToProject(project);
  }, [archiveProjectData]);

  useEffect(() => {
    if (!archiveProjectError) return;
    setShowErrorMsg(archiveProjectError.message);
    client.refetchQueries({
      include: [GetProjectDocument],
    });
  }, [archiveProjectError]);

  const [
    unarchiveProject,
    { data: unarchiveProjectData, error: unarchiveProjectError },
  ] = useUnarchiveProjectMutation({
    refetchQueries: ['listProjects'],
  });

  useEffect(() => {
    if (!unarchiveProjectData) return;
    if (unarchiveProjectData.unarchiveProject) {
      setSelectedProject(unarchiveProjectData.unarchiveProject as Project);
    }
    navigation.navigateToProject(project);
  }, [unarchiveProjectData]);

  useEffect(() => {
    if (!unarchiveProjectError) return;
    setShowErrorMsg(unarchiveProjectError.message);
    client.refetchQueries({
      include: [GetProjectDocument],
    });
  }, [unarchiveProjectError]);

  const [completeWebTutorialFlows] = useCompleteWebTutorialFlowsMutation({
    refetchQueries: [{ query: GetMeDocument }, 'getMe'],
  });

  const [
    deleteProject,
    { data: deleteProjectData, error: deleteProjectError },
  ] = useDeleteProjectMutation({
    refetchQueries: ['listProjects'],
  });

  useEffect(() => {
    if (!deleteProjectData) return;
    navigation.navigate('projects-stack', { screen: 'allprojects' });
  }, [deleteProjectData]);

  useEffect(() => {
    if (!deleteProjectError) return;
    setShowConfirmDelete(false);
    toastRef.current?.show('Delete project failed.', {
      duration: 2000,
    });
    client.refetchQueries({
      include: [GetProjectDocument],
    });
  }, [deleteProjectError]);

  const handleDeleteProject = useCallback(() => {
    deleteProject({
      variables: {
        id: project.id || '',
      },
    });
  }, [deleteProject, project.id]);

  const [createInviteMutation, { data: createInviteMutationData }] =
    useCreateInviteMutation();

  useEffect(() => {
    if (!createInviteMutationData) return;
    const { createInvite } = createInviteMutationData;
    if (createInvite) {
      const formattedLink = createInvite.replace(
        'register/register-with-email',
        'invited-user'
      );
      copyToClipboard(formattedLink);
    }
  }, [createInviteMutationData]);

  const [
    updateProject,
    { data: updateProjectData, error: updateProjectError },
  ] = useUpdateProjectMutation({
    refetchQueries: ['listProjects', 'listProjectsRecent'],
  });

  useEffect(() => {
    if (!updateProjectData) return;
    setReferencePinStatus(null);
    setSelectedProject({
      ...project,
      settings: updateProjectData.updateProject?.settings,
    });
  }, [updateProjectData]);

  useEffect(() => {
    if (!updateProjectError) return;
    setSelectedProject({
      ...project,
      settings: {
        id: '',
        pin: referencePinStatus ?? false,
      },
    });
    console.log('err updating project', updateProjectError);
  }, [updateProjectError]);

  const copyToClipboard = async (link: string) => {
    if (!link) return; // Early return if link is falsy
    await Clipboard.setStringAsync(link);

    toastRef.current?.show('Copied', {
      duration: 2000,
    });
  };

  const togglePinProject = async () => {
    const {
      teamId,
      name,
      address,
      latitude,
      longitude,
      description,
      color,
      icon,
      settings,
    } = project;
    const projectName = name.trim();
    const currentPinStatus = settings?.pin ?? false;

    if (referencePinStatus === null) {
      setReferencePinStatus(currentPinStatus);
    }

    const members = project.members.map((u) => ({
      userId: u.user.id,
      role: u.role,
    }));

    const attributes = {
      address,
      color,
      description,
      icon,
      latitude,
      longitude,
      members,
      name: projectName,
      teamId,
      pin: !currentPinStatus,
    };
    if (project.id) {
      setSelectedProject({
        ...project,
        settings: {
          id: '',
          pin: !currentPinStatus,
        },
      });
      updateProject({
        variables: {
          id: project.id,
          attributes,
        },
      });
    }
  };

  const handleLeaveProject = useCallback(() => {
    leaveProject({ variables: { id: project.id || '' } });
  }, [leaveProject, project.id]);

  const menuList: ComponentProps<typeof PopupIcon>['popupProps']['menuList'] = [
    ...(!project.archivedAt
      ? [
          {
            iconName: project.settings?.pin ? 'Unpin' : 'Pin',
            name: project.settings?.pin ? 'Unpin' : 'Pin',
            onSelect: () => {
              togglePinProject();
            },
          },
        ]
      : []),

    ...((isAdminOrHigher || isTeamOwnerOrAdmin || isTeamMember) &&
    !project.archivedAt
      ? [
          {
            iconName: 'Edit',
            name: t('projects.edit.title'),
            onSelect: () => {
              if (shouldShowPaywallForProject(project)) {
                setPaywallTeam(project?.team ?? undefined);
                setPaywallModalOpen(true);
                return;
              }
              setEditProjectUUId(project.uuid);
              setIsCreateProjectOpen(true);
            },
          },
        ]
      : []),
    ...((isEditorOrHigher || isTeamOwnerOrAdmin || isTeamMember) &&
    !project.archivedAt
      ? [
          {
            iconName: 'Duplicate',
            disable:
              !(isTeamMember || isTeamOwnerOrAdmin || isProjectAdmin) ||
              (teamPersonal && !isPersonalProjectsLeft),
            name: 'Duplicate',
            onSelect: () => {
              if (shouldShowPaywallForProject(project)) {
                setPaywallTeam(project?.team ?? undefined);
                setPaywallModalOpen(true);
                return;
              }
              setShowProjectColorAndIconModal(true);
              navigation.navigate('projects-stack', {
                screen: 'duplicate',
                params: { projectUUId: project.uuid },
              });
            },
          },
        ]
      : []),
    // ...((isEditorOrHigher || isTeamOwnerOrAdmin) &&
    // !project.archivedAt &&
    // members?.length
    //   ? [{ iconName: 'Chat', name: 'Announcement', isComingSoon: true }]
    //   : []),

    ...(!project.archivedAt
      ? [
          {
            iconName: 'Link',
            name: t('projects.linkInvite.copyLinkToInvite'),
            onSelect: () => {
              if (shouldShowPaywallForProject(project)) {
                setPaywallTeam(project?.team ?? undefined);
                setPaywallModalOpen(true);
                return;
              }
              if (!me?.completedWebTutorialFlows?.includes('invite_member')) {
                completeWebTutorialFlows({
                  variables: {
                    flowType: WebTutorialFlow.InviteMember,
                  },
                });
              }
              createInviteMutation({ variables: { projectId: project.uuid } });
            },
          },
          {
            iconName: 'FileText',
            disable: !projectReportFlag,
            name: 'Project Report',

            onSelect: async () => {
              const baseURL = window.location.origin;
              await Linking.openURL(
                `${baseURL}/projects/project-report?projectUUId=${project?.uuid}`
              );
            },
          },
        ]
      : []),

    ...((!project.archivedAt &&
      !isTeamOwnerOrAdmin &&
      (isMember || isEditorOrHigher || isViewer)) ||
    (isTeamOwnerOrAdmin && isTeamMember && !isOwner)
      ? [
          {
            iconName: 'LogOut',
            name: 'Leave',
            onSelect: () => setShowLeaveProjectModal(true),
          },
        ]
      : []),
    ...((isTeamOwnerOrAdmin || isAdminOrHigher || isOwner) && !isTeamExpired
      ? [
          {
            iconName: 'Archive',
            name: project.archivedAt ? 'Restore' : 'Archive',
            onSelect: () =>
              project.archivedAt
                ? unarchiveProject({ variables: { id: project.id || '' } })
                : archiveProject({ variables: { id: project.id || '' } }),
          },
        ]
      : []),
    ...((isAdminOrHigher || isTeamOwnerOrAdmin) && !project.archivedAt
      ? [
          {
            iconName: 'Trash2',
            name: 'Delete',
            isRed: true,
            onSelect: () => {
              setShowConfirmDelete(true);
            },
          },
        ]
      : []),
  ];

  // Mirrors PopupItem's own disabled logic (`isComingSoon || disable`) so a
  // menu that only holds non-actionable items never renders a clickable trigger.
  const hasExecutableProjectOption = menuList.some(
    (item) => !item.isComingSoon && !item.disable
  );

  return (
    <>
      {hasExecutableProjectOption && (
        <PopupIcon
          enableHoverEffect={true}
          needChangeBgColorHovered={true}
          {...props}
          name='MoreVertical'
          variant={popSize === 'big' ? 'l' : 'm'}
          padding='xxs'
          color='textPrimary'
          popupProps={{
            menuList,
            position:
              popPosition === 'top' ? ['left', 'top'] : ['left', 'bottom'],
            offset: [0, theme.spacing.m],
          }}
        />
      )}
      {showLeaveProjectModal && (
        <ConfirmModal
          showModal={showLeaveProjectModal}
          onClose={() => setShowLeaveProjectModal(false)}
          onPress={handleLeaveProject}
          buttonText={t('models:leaveProject.title')}
          title={t('models:leaveProject.title')}
          message={t('models:leaveProject.message')}
        />
      )}
      {showConfirmDelete && (
        <ConfirmModal
          showModal={showConfirmDelete}
          onClose={() => setShowConfirmDelete(false)}
          onPress={handleDeleteProject}
          buttonText={t('models:deleteProject.deleteProjectBtn')}
          title={t('models:deleteProject.deleteProject')}
          message={t('models:deleteProject.deleteMessage')}
          // TA-10949 #36: X is the only dismissal action here, matching the
          // rest of the app's destructive-confirmation modals (isAlert hides
          // ConfirmModal's Cancel button).
          isAlert={true}
        />
      )}
      {showErrorMsg !== '' && (
        <ConfirmModal
          isAlert={true}
          showModal={showErrorMsg !== ''}
          onClose={() => setShowErrorMsg('')}
          onPress={() => {
            setShowErrorMsg('');
          }}
          buttonText='   OK   '
          title='Error'
          message={showErrorMsg}
        />
      )}
      <Toast ref={toastRef} />
    </>
  );
};
